import type { Result } from "@pdfking/errors"
import { Failure, ResultMap, Success } from "@pdfking/errors"
import type { EnumType, EnumUnion } from "@pdfking/structs"
import { ArrayQ, NullQ, RecordQ } from "@pdfking/structs"
import {
  JSONEncode,
  KeyDrop,
  Keys,
  Map,
  RecordMap,
  RecordMerge,
  ToArray,
  Values,
  Zip,
} from "@pdfking/tfunc"

import type {
  AnyNode,
  ArrayForm,
  AtomForm,
  ChoiceForm,
  Choices,
  EnumForm,
  NodeOf,
  ObjectForm,
  ValueOf,
} from "#public"
import type { BaseError, ValidationResult } from "#public"
import type { Validator } from "#public"

import { ConfirmAllValid } from "../../internal/ConfirmAllValid"
import { UseInitial } from "../../internal/UseInitial"
import { FormType } from "../types/FormType"
import { AlmostNullQ } from "../utils/AlmostNullQ"
import { ChoiceFlat } from "../utils/ChoiceFlat"

export type FieldConfig<T> = {
  validate: (input: unknown) => Result<T>
  format?: (value: T) => string
  initial?: () => unknown
  optional?: boolean
  suggestions?: Record<string, string | undefined>
}

type ContainerConfig = {
  initial?: () => unknown
  format?: (value: unknown) => string
}

type AnyValidator<T> = Validator<T, EnumType<string, BaseError>>

// An un-validated node reads as "required": same message, one fewer error.
const INITIAL = Failure("REQUIRED_ERROR", { input: undefined })

// Clamp an unknown input into an array of length [min, max]: a non-array
// becomes [], a longer list truncates to `max`, a shorter one pads with
// `undefined` up to `min` (so every slot picks up the element default).
const Truncate = (input: unknown, min: number, max: number): unknown[] => {
  const items = (ArrayQ(input) ? input : []).slice(0, max)
  while (items.length < min) items.push(undefined)
  return items
}

type FieldValue<T, Optional extends boolean> = Optional extends true ? T | undefined : T

export const CreateFormField = <T, Optional extends boolean = false>({
  validate: cast,
  format = JSONEncode,
  initial,
  optional = false as Optional,
  suggestions,
}: FieldConfig<T> & { optional?: Optional }): AtomForm<FieldValue<T, Optional>> => {
  type Out = FieldValue<T, Optional>

  const make = (input: unknown, result: Result<Out>): AtomForm<Out> =>
    ({
      enum: FormType.ATOM,
      initial,
      optional,
      suggestions,
      // `""`/`undefined`/`undefined` are the same "no input": store `undefined` so a
      // cleared field never keeps a stale empty string.
      input: UseInitial(input),
      validate,
      format: (value: Out): string => (AlmostNullQ(value) ? "" : format(value as T)),
      ...result,
    }) as AtomForm<Out>
  const validate = (input: unknown): AtomForm<Out> => {
    const value = UseInitial(input, initial)
    if (NullQ(value))
      return make(
        input,
        optional ? Success(undefined as Out) : Failure("REQUIRED_ERROR", { input })
      )
    return make(input, cast(value) as Result<Out>)
  }
  return make(undefined, optional ? Success(undefined as Out) : (INITIAL as Result<Out>))
}

// Config for a choice field. `validate` runs as a post-cast over the resolved
// value(s) (e.g. membership/length/required checks); `multiple` collects a list.
type ChoiceValue<T, Multiple extends boolean> = Multiple extends true ? T[] : T

export type ChoiceConfig<T, Multiple extends boolean = false> = {
  // loose internally; the public field constructors keep the precise key→value
  // type and the node re-narrows to the element type.
  choices: Choices<T>
  validate?: (value: unknown) => Result<ChoiceValue<T, Multiple>>
  format?: (value: T) => string
  initial?: () => unknown
  multiple?: Multiple
}

// A choice field resolves the input key(s) against `choices` (array-ifying for
// `multiple`), failing `CHOICE_ERROR` on an unknown key, then returns the list
// when `multiple` or the first otherwise — finally running the post-cast.
export const CreateFormChoice = <T, const Multiple extends boolean = false>({
  choices,
  validate: cast = Success as (value: unknown) => Result<ChoiceValue<T, Multiple>>,
  format = JSONEncode,
  initial,
  multiple = false as Multiple,
}: ChoiceConfig<T, Multiple>): ChoiceForm<ChoiceValue<T, Multiple>> => {
  type Out = ChoiceValue<T, Multiple>
  const flat = ChoiceFlat(choices)
  const make = (input: unknown, result: Result<Out>): ChoiceForm<Out> =>
    ({
      enum: FormType.CHOICE,
      initial,
      choices,
      multiple,
      input,
      validate,
      format,
      ...result,
    }) as ChoiceForm<Out>
  const resolve = (value: unknown): Result<unknown> => {
    const resolved: unknown[] = []
    for (const key of ToArray(value)) {
      const hit = flat[key as string]
      if (NullQ(hit))
        return Failure("CHOICE_ERROR", {
          value: key,
          // the available options as already-formatted labels, not raw values
          choices: Values(flat).map(v => format(v as T)),
          input: value,
        })
      resolved.push(hit)
    }
    return Success((multiple ? resolved : resolved[0]) as Out)
  }
  // a choice field stores the resolved-from-default input so a renderer can show
  // the selected key(s) even when the value came from `initial`.
  const validate = (raw: unknown): ChoiceForm<Out> => {
    const input = UseInitial(raw, initial)
    if (NullQ(input)) return make(input, Failure("REQUIRED_ERROR", { input }))
    return make(input, ResultMap(resolve(input), cast) as Result<Out>)
  }
  return make(undefined, INITIAL)
}

export const CreateFormArray = <E>(
  field: NodeOf<E>,
  {
    initial,
    format = JSONEncode,
    validators = [],
    min = 0,
    max = 1000,
  }: ContainerConfig & {
    validators?: AnyValidator<E[]>[]
    min?: number
    max?: number
  } = {}
): ArrayForm<E> => {
  const make = (
    input: unknown,
    results: NodeOf<E>[],
    result: ValidationResult<E[]>
  ): ArrayForm<E> =>
    ({
      enum: FormType.ARRAY,
      initial,
      input,
      validate,
      format,
      results,
      min,
      max,
      ...result,
    }) as ArrayForm<E>
  const validate = (input: unknown): ArrayForm<E> => {
    // a undefined/blank input falls back to the default list; each slot (incl. the
    // `undefined` padding up to `min`) then picks up the element field's default.
    const value = UseInitial(input, initial)
    const children = Truncate(value, min, max).map(item => field.validate(item)) as NodeOf<E>[]
    const results = validators.reduce(
      (r: Parameters<AnyValidator<E[]>>[0], v) => v(r, input),
      children as Parameters<AnyValidator<E[]>>[0]
    )
    // a validator refines value/error, never the raw `input` — keep the child's
    // (its error detail may carry an unrelated `input` field).
    const merged = [
      ...Map(([l, r]) => RecordMerge(l, r, { input: l.input }), Zip(children, results)),
    ] as NodeOf<E>[]
    return make(input, merged, ConfirmAllValid(merged) as ValidationResult<E[]>)
  }
  return make(undefined, [field], INITIAL as ValidationResult<E[]>)
}

export const CreateFormObject = <F extends Record<string, AnyNode>>(
  fields: F,
  {
    initial,
    format = JSONEncode,
    validators = [],
  }: ContainerConfig & {
    validators?: AnyValidator<{ [K in keyof F]: ValueOf<F[K]> }>[]
  } = {}
): ObjectForm<{ [K in keyof F]: ValueOf<F[K]> }> => {
  type T = { [K in keyof F]: ValueOf<F[K]> }
  const make = (
    input: unknown,
    results: Record<string, AnyNode>,
    result: ValidationResult<T>
  ): ObjectForm<T> =>
    ({
      enum: FormType.OBJECT,
      initial,
      input,
      validate,
      format,
      results,
      ...result,
    }) as ObjectForm<T>
  const validate = (input: unknown): ObjectForm<T> => {
    const data = RecordQ(input) ? input : undefined
    const children = RecordMap((form, key) => form.validate(data?.[key]), fields) as Record<
      string,
      AnyNode
    >
    const results = validators.reduce(
      (r, v) => v(r, input),
      children as unknown as Parameters<AnyValidator<T>>[0]
    )
    // a validator refines value/error, never the raw `input` — keep the child's
    // (its error detail may carry an unrelated `input` field).
    const merged = RecordMap(
      (child, key) =>
        RecordMerge(child, (results as Record<string, ValidationResult<unknown>>)[key], {
          input: child.input,
        }),
      children
    ) as Record<string, AnyNode>
    return make(input, merged, ConfirmAllValid(merged) as ValidationResult<T>)
  }
  return make(undefined, fields, INITIAL as ValidationResult<T>)
}

// A discriminated union over `variants` (tag → object form). The input is the
// plain union member `{ enum, ...fields }`: the tag selects the variant, the rest
// validates through its form. Only the selected variant is (re)validated — the
// others keep their proto state here and their live state in the reactive twin.
// `label` names a tag (defaults to the tag itself).
export const CreateFormEnum = <F extends Record<string, ObjectForm<Record<string, unknown>>>>(
  variants: F,
  {
    initial,
    format = JSONEncode,
    label = String,
  }: ContainerConfig & { label?: (key: keyof F & string) => string } = {}
): EnumForm<EnumUnion<{ [K in keyof F]: ValueOf<F[K]> }>> => {
  type T = EnumUnion<{ [K in keyof F]: ValueOf<F[K]> }>
  const make = (
    input: unknown,
    selected: string | undefined,
    results: Record<string, AnyNode>,
    result: ValidationResult<unknown>
  ): EnumForm<T> =>
    ({
      enum: FormType.ENUM,
      initial,
      input,
      validate,
      format,
      label,
      selected,
      results,
      ...result,
    }) as EnumForm<T>
  const validate = (raw: unknown): EnumForm<T> => {
    const input = UseInitial(raw, initial)
    const selected = RecordQ(input) ? (input.enum as string) : undefined
    if (NullQ(selected))
      return make(input, undefined, variants, Failure("REQUIRED_ERROR", { input }))
    const variant = variants[selected]
    if (NullQ(variant))
      return make(
        input,
        undefined,
        variants,
        Failure("CHOICE_ERROR", { value: selected, choices: Keys(variants).map(label), input })
      )
    const child = variant.validate(KeyDrop(input as Record<string, unknown>, ["enum"]))
    return make(
      input,
      selected,
      RecordMerge(variants, { [selected]: child }),
      ResultMap(ConfirmAllValid({ [selected]: child }), v =>
        Success({ enum: selected, ...(v[selected] as object) })
      )
    )
  }
  return make(undefined, undefined, variants, INITIAL)
}
